// Angular
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Afidium
import { WORDING } from './config/wording';
import { ProcessingEmailService } from '../../services/processing-email.service';
import { MailBreadcrumbService } from '../../services/email-breadcrumb.service';
import { IBreadcrumbItem } from 'libraries/service/breadcrumb/IBreadcrumbItem';
import { ITaskRequest } from 'libraries/service/resources/model/processing/ITaskRequest';

import { Yii } from "libraries/interfaces/Yii";
import { ISendEmailResponse } from 'libraries/service/resources/model/processing/ISendEmailResponse';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { ILoader } from 'libraries/component/loader/ILoader';
import { IEmail, IEmailRequest } from 'app_sasha/shared/interfaces/IEmail';
import { ProcessingService } from 'libraries/service/processing/processing.service';
import { ProcessingEmailResourceService } from 'app_sasha/shared/resources/processing-email-resource/processing-email-resource.service';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { IGetBookingsCommonResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { LanguageService } from "libraries/service/language/language.service";

@Component({
    selector: 'mail-validation',
    template: `
        <div class="app-wrapper app-sasha">
            <breadcrumb [items]="breadcrumbItems"></breadcrumb>

            <card>
                <div class="card-header-content">
                    <h1>{{ wording.header }}</h1>

                    <div>
                        <back-button></back-button>
                        <btn [label]="wording.previousView" (click)="emailFormView()" type="actionDo"></btn>
                        <btn [label]="wording.validate" (click)="sendEmail()" type="actionPrincipal" class="e2e-btn-send-email"></btn>
                    </div>
                </div>
                <div class="card-body-content card-marge-inside">
                    <processing-validation
                        [codes]="codes"
                        [syscodes]="syscodes"
                        (onChangeFolder)="loadBooking($event)">

                        <notification class="sep-top sep-bottom" *ngIf="notification.active"
                            [type]="notification.type">
                            {{ notification.message }}
                        </notification>

                        <ng-container *ngIf="emailValidation && !loader.active">
                            <div *ngIf="!notification.active">
                                <div *ngIf="emailValidation.from" class="sep-top">
                                    <span>{{ wording.from }} </span>
                                    <span class="mail-value">{{ emailValidation.from }}</span>
                                </div>

                                <div *ngIf="emailValidation.replyTo" class="sep-top">
                                    <span>{{ wording.replyTo }} </span>
                                    <span class="mail-value">{{ emailValidation.replyTo }}</span>
                                </div>

                                <div *ngIf="emailValidation.to" class="sep-top">
                                    <span>{{ wording.to }} </span>
                                    <span class="mail-value">{{ emailValidation.to }}</span>
                                </div>

                                <div *ngIf="emailValidation.cc.length > 0" class="sep-top">
                                    <span>{{ wording.cc }} </span>
                                    <span class="mail-value">{{ emailValidation.cc }}</span>
                                </div>

                                <div *ngIf="emailValidation.bcc.length > 0" class="sep-top">
                                    <span>{{ wording.bcc }} </span>
                                    <span class="mail-value">{{ emailValidation.bcc }}</span>
                                </div>

                                <div *ngIf="emailValidation.subject" class="sep-top">
                                    <span>{{ wording.subject }} </span>
                                    {{ emailValidation.subject }}
                                </div>
                                <div *ngIf="emailValidation.body"
                                    class="sep-top"
                                    [innerHTML]="emailValidation.body | safeHtml"></div>
                            </div>
                        </ng-container>

                        <loader *ngIf="loader.active" [message]="loader.message"></loader>
                    </processing-validation>
                </div>
            </card>
        </div>
    `,
    styles: [`
        notification.sep-top {
            display: block;
        }

        .folder-number {
            display: flex;
            justify-content: space-between;
        }

        .link.sep-right {
            display: inline-block;
        }

        .mail-value {
            font-weight: bold;
        }
    `]
})
export class EmailValidationComponent implements OnInit {
    public wording = WORDING;

    public syscodeIndex: number = 0;
    public syscodesLength: number = 1;
    public syscodes: string[] = [];
    public codes: string[] = [];

    public emailValidation: IEmail;
    public breadcrumbItems: IBreadcrumbItem[] = [];
    public notification: INotification = {
        active: false,
        type: NotificationType.Error,
        message: null
    };
    public loader: ILoader = {
        active: false,
        message: null
    };

    constructor(
        private readonly router: Router,
        private readonly processingEmailService: ProcessingEmailService,
        private readonly mailBreadcrumbService: MailBreadcrumbService,
        private readonly processingService: ProcessingService,
        private readonly processingEmailResourceService: ProcessingEmailResourceService,
        private readonly titleService: TitleService,
        private readonly languageService: LanguageService,
    ) { }

    public ngOnInit(): void {
        this.breadcrumbItems = this.mailBreadcrumbService.breadcrumbValidation();
        this.codes = this.processingService.bookings.map((booking: IGetBookingsCommonResponse): string => booking.code);
        this.syscodes = this.processingService.bookings.map((booking: IGetBookingsCommonResponse): string => booking.syscode);
        this.titleService.setTitle(Yii.t("wui-sasha-js", "Prévisualisation des emails"));

        this.loader = {
            active: true,
            message: this.wording.loadingEmail
        };

        // Get initial preview
        this.loadBooking(this.syscodes[0]);
    }

    public async emailFormView(): Promise<void> {
        await this.router.navigate(['/sasha/processing-email/form'], { queryParamsHandling: 'preserve' });
    }

    public sendEmail(): void {
        const email = this.processingEmailService.form;
        const task: ITaskRequest[] =
            this.processingService.bookings.map((booking) => ({ 'booking': { syscode: booking.syscode } }));

        this.loader = {
            active: true,
            message: this.wording.loadingSendEmail
        };

        this.processingEmailResourceService.sendEmail({
            title: email.subject || Yii.t('wui-sasha-js', "Envoi d'e-mail"),
            type: 'email',
            task,
            parameter: {
                cc: email.cc,
                bcc: email.bcc,
                replyTo: email.replyTo,
                body: email.body,
                subject: email.subject,
                modelName: email.modelName,
                from: email.from,
                language: this.languageService.userLanguage,
            }
        }).subscribe(
            (response: ISendEmailResponse) => this.router.navigate(['/sasha/processing/', response.id]),
            (error: Error) => this.notification = {
                active: true,
                type: NotificationType.Error,
                message: error.message
            },
            () => this.loader.active = false
        );
    }

    public loadBooking(syscode: string): void {
        this.loader = {
            active: true,
            message: this.wording.loadingEmail
        };

        this.notification.active = false;

        const values = this.processingEmailService.form;
        const formValues: IEmailRequest = {
            cc: values.cc,
            bcc: values.bcc,
            subject: values.subject,
            body: values.body,
            replyTo: values.replyTo,
            from: values.from
        };

        this.processingEmailResourceService.getPreview(syscode, formValues)
            .subscribe(
                (email: IEmail) => this.emailValidation = {
                    ...this.emailValidation,
                    ...email
                },
                (error: Error) => {
                    this.loader.active = false;

                    this.notification = {
                        active: true,
                        type: NotificationType.Error,
                        message: error.message
                    };
                },
                () => this.loader.active = false
            );
    }
}
